import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Share2, Copy, Check, MessageCircle, Facebook } from 'lucide-react';
import { toast } from 'sonner';

const SHARE_URL = 'https://www.mart-mineral.com/golden-oz';

const content = {
  he: {
    title: 'שתפו את היצירה',
    subtitle: 'עזרו לנו להפיץ את זכר 7.10 ואת היצירה המינרלית של נחל עוז',
    whatsapp: 'WhatsApp',
    facebook: 'Facebook',
    copy: 'העתק קישור',
    copied: 'הקישור הועתק!',
    native: 'שיתוף מהיר',
    shareText:
      'גולדן עוז – מיצב אומנותי מינרלי לזכר נופלי 7.10 בנחל עוז. יצירה ייחודית של M\'art בציפוי מיקרומינרל.',
    shareTitle: 'גולדן עוז - Golden Oz | M\'art',
  },
  en: {
    title: 'Share the Artwork',
    subtitle: 'Help us spread the memory of October 7 and the mineral artwork of Nahal Oz',
    whatsapp: 'WhatsApp',
    facebook: 'Facebook',
    copy: 'Copy link',
    copied: 'Link copied!',
    native: 'Quick share',
    shareText:
      'Golden Oz - mineral memorial art installation for the fallen of October 7 at Nahal Oz. A unique artwork by M\'art in microMineral coating.',
    shareTitle: 'Golden Oz | M\'art',
  },
};

export const ShareArtwork = () => {
  const { isRTL } = useLanguage();
  const [copied, setCopied] = useState(false);
  const t = isRTL ? content.he : content.en;

  const encodedUrl = encodeURIComponent(SHARE_URL);
  const encodedText = encodeURIComponent(`${t.shareText} ${SHARE_URL}`);

  const whatsappHref = `https://wa.me/?text=${encodedText}`;
  const facebookHref = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      toast.success(t.copied);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      // Fallback: select-and-copy via temp textarea
      const ta = document.createElement('textarea');
      ta.value = SHARE_URL;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        toast.success(t.copied);
        setTimeout(() => setCopied(false), 2500);
      } catch (err) {
        // ignore
      }
      document.body.removeChild(ta);
    }
  };

  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: t.shareTitle,
          text: t.shareText,
          url: SHARE_URL,
        });
      } catch (e) {
        // User cancelled
      }
    } else {
      handleCopy();
    }
  };

  const hasNativeShare = typeof navigator !== 'undefined' && !!navigator.share;

  return (
    <section
      className="px-4 pb-16 pt-2"
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="share-artwork-section"
    >
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-100px' }}
        transition={{ duration: 0.6 }}
        className="max-w-3xl mx-auto bg-white/80 backdrop-blur-sm rounded-2xl border border-[#B8862C]/20 p-6 sm:p-8 shadow-md"
      >
        <div className="text-center mb-5">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#E5B53A]/15 border border-[#E5B53A]/30 mb-3">
            <Share2 className="w-5 h-5 text-[#B8862C]" />
          </div>
          <h3 className="text-xl sm:text-2xl font-bold text-[#3A2E18] mb-1.5">
            {t.title}
          </h3>
          <p className="text-sm text-[#6B5A3A] max-w-xl mx-auto leading-relaxed">
            {t.subtitle}
          </p>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2.5 sm:gap-3">
          {/* WhatsApp */}
          <a
            href={whatsappHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-whatsapp"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] text-white text-sm font-medium shadow-sm hover:bg-[#1ebe5b] hover:shadow-md transition-all"
          >
            <MessageCircle className="w-4 h-4" />
            <span>{t.whatsapp}</span>
          </a>

          {/* Facebook */}
          <a
            href={facebookHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-facebook"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#1877F2] text-white text-sm font-medium shadow-sm hover:bg-[#0e63d3] hover:shadow-md transition-all"
          >
            <Facebook className="w-4 h-4" />
            <span>{t.facebook}</span>
          </a>

          {/* Copy link */}
          <button
            onClick={handleCopy}
            data-testid="share-copy"
            className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium shadow-sm transition-all border ${
              copied
                ? 'bg-[#3A2E18] text-white border-[#3A2E18]'
                : 'bg-white text-[#3A2E18] border-[#B8862C]/30 hover:bg-[#FAF6EE] hover:border-[#B8862C]/50 hover:shadow-md'
            }`}
          >
            {copied ? (
              <Check className="w-4 h-4" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
            <span>{copied ? t.copied : t.copy}</span>
          </button>

          {/* Native share (mobile-friendly) */}
          {hasNativeShare && (
            <button
              onClick={handleNativeShare}
              data-testid="share-native"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-gradient-to-br from-[#E5B53A] to-[#B8862C] text-white text-sm font-medium shadow-sm hover:shadow-md transition-all"
            >
              <Share2 className="w-4 h-4" />
              <span>{t.native}</span>
            </button>
          )}
        </div>

        {/* Direct link display */}
        <div className="mt-5 text-center">
          <code className="inline-block px-3 py-1.5 rounded-lg bg-[#FAF6EE] border border-[#B8862C]/15 text-xs sm:text-sm text-[#8C661F] font-mono select-all">
            {SHARE_URL}
          </code>
        </div>
      </motion.div>
    </section>
  );
};

export default ShareArtwork;
